import styled, { css } from "styled-components";

import { previewTheme } from "../preview";

/** Colours lifted from the generated PDF so the mock reads as the real thing. */
export const pdfTheme = {
  navy: "#426393",
  lightBlue: "#a5c9eb",
  gray: "#f0f0f0",
  lightGray: "#e2e2e4",
  paper: "#ffffff",
  ink: "#1f1f1f",
};

/**
 * Settings beside the document mock on wide screens, matching the checkout
 * preview. Below the breakpoint it collapses to one column, so source order
 * (toggles, then preview) is also the stacked order.
 */
export const StyledPdfColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  align-items: start;

  @media (min-width: 1200px) {
    grid-template-columns: minmax(340px, 2fr) minmax(0, 3fr);
  }
`;

export const StyledPreviewPanel = styled.div`
  background-color: ${previewTheme.canvas};
  border: 1px solid ${previewTheme.hairline};
  border-radius: 16px;
  padding: 16px;
`;

export const StyledPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

export const StyledPanelTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${previewTheme.ink};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  margin: 0;

  i {
    color: ${previewTheme.indigo};
    font-size: 14px;
  }
`;

export const StyledVariantPill = styled.span`
  background-color: ${previewTheme.indigoSoft};
  color: ${previewTheme.indigo};
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
`;

/** Explains the download behaviour, which is a flow change, not a page change. */
export const StyledFlowNote = styled.div<{ $tone?: "info" | "warn" }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 12px;
  font-size: 12px;
  line-height: 1.5;

  ${({ $tone }) =>
    $tone === "warn"
      ? css`
          background-color: #fef6e7;
          color: #8a5b00;
        `
      : css`
          background-color: ${previewTheme.indigoSoft};
          color: ${previewTheme.indigo};
        `};

  i {
    font-size: 12px;
    margin-top: 2px;
  }
`;

/**
 * The sheet itself. Everything inside is deliberately tiny — this is a shape
 * preview, not a readable document, so the whole page fits without scrolling.
 */
export const StyledPage = styled.div`
  background-color: ${pdfTheme.paper};
  border: 1px solid ${previewTheme.hairline};
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(23, 20, 63, 0.08);
  min-height: 520px;
  padding: 16px 18px 12px;
  display: flex;
  flex-direction: column;
  color: ${pdfTheme.ink};
  font-size: 8px;
  line-height: 1.45;
`;

export const StyledPageBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const StyledPageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid ${pdfTheme.lightGray};
  padding-bottom: 6px;
  margin-bottom: 10px;
`;

export const StyledLogoBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 20px;
  border: 1px dashed ${previewTheme.hairline};
  border-radius: 3px;
  color: ${previewTheme.muted};
  font-size: 7px;
  font-weight: 700;
  letter-spacing: 0.06em;
`;

export const StyledFacilityMeta = styled.div`
  color: ${previewTheme.body};
  font-size: 7px;
  text-align: right;
`;

export const StyledDocTitle = styled.p`
  && {
    color: ${pdfTheme.ink};
  }
  font-size: 11px;
  font-weight: 700;
  text-align: center;
  margin: 0 0 8px;
`;

export const StyledParagraph = styled.p`
  && {
    color: ${previewTheme.body};
  }
  font-size: 7px;
  line-height: 1.5;
  text-align: justify;
  margin: 0 0 6px;
`;

export const StyledPatientGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px 10px;
  border: 1px solid ${pdfTheme.lightGray};
  border-radius: 3px;
  padding: 6px 8px;
  margin-bottom: 8px;
`;

export const StyledPatientItem = styled.div`
  font-size: 7px;

  span {
    display: block;
    color: ${previewTheme.muted};
    font-size: 6px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  strong {
    color: ${pdfTheme.ink};
    font-weight: 600;
  }
`;

/* Wraps a block that only exists because of a toggle, and names that toggle. */
export const StyledMarkedBlock = styled.div`
  position: relative;
  border: 1px dashed ${previewTheme.indigoBorder};
  border-radius: 6px;
  padding: 10px 6px 6px;
  margin-bottom: 8px;
`;

export const StyledChangeTag = styled.span`
  position: absolute;
  top: -7px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background-color: ${previewTheme.indigoSoft};
  color: ${previewTheme.indigo};
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.02em;

  i {
    font-size: 7px;
  }
`;

export const StyledTableCaption = styled.p`
  && {
    color: ${pdfTheme.ink};
  }
  font-size: 8px;
  font-weight: 700;
  text-align: center;
  margin: 0 0 4px;
`;

/** Column widths come from the `$columns` grid template each table passes in. */
export const StyledTable = styled.div<{ $columns: string }>`
  --pdf-columns: ${({ $columns }) => $columns};
  border: 1px solid ${pdfTheme.lightGray};
  border-radius: 3px;
  overflow: hidden;
  font-size: 7px;
`;

type RowTone = "head" | "gray" | "blue" | "navy" | "plain";

const rowTones: Record<RowTone, ReturnType<typeof css>> = {
  head: css`
    background-color: ${pdfTheme.lightGray};
    font-weight: 700;
    letter-spacing: 0.03em;
  `,
  gray: css`
    background-color: ${pdfTheme.gray};
  `,
  blue: css`
    background-color: ${pdfTheme.lightBlue};
  `,
  navy: css`
    background-color: ${pdfTheme.navy};
    color: ${pdfTheme.paper};
    font-weight: 700;
    font-size: 8px;
  `,
  plain: css`
    background-color: ${pdfTheme.paper};
  `,
};

export const StyledTableRow = styled.div<{ $tone?: RowTone }>`
  display: grid;
  grid-template-columns: var(--pdf-columns);
  gap: 4px;
  padding: 3px 6px;
  border-top: 1px solid ${pdfTheme.lightGray};

  &:first-child {
    border-top: none;
  }

  ${({ $tone }) => rowTones[$tone || "plain"]};
`;

export const StyledTableCell = styled.span<{ $right?: boolean }>`
  text-align: ${({ $right }) => ($right ? "right" : "left")};
  overflow-wrap: anywhere;
`;

export const StyledNonCovered = styled.div`
  border: 1px solid ${pdfTheme.lightGray};
  border-radius: 3px;
  padding: 4px 6px;
  margin-top: 4px;
  font-size: 7px;

  strong {
    display: block;
    margin-bottom: 2px;
  }

  div {
    display: flex;
    justify-content: space-between;
    color: ${previewTheme.body};
  }
`;

/* Grey prompt-pay callout with the phone artwork on the right. */
export const StyledPromptPayBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background-color: ${pdfTheme.gray};
  border-radius: 3px;
  padding: 8px 10px;
  margin-top: 8px;
  font-size: 7px;
  line-height: 1.5;
  color: ${previewTheme.body};

  strong {
    color: ${pdfTheme.ink};
  }
`;

export const StyledPhoneMock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 26px;
  height: 46px;
  border: 1.5px solid ${previewTheme.muted};
  border-radius: 5px;
  color: ${previewTheme.muted};

  i {
    font-size: 11px;
  }
`;

export const StyledNoteText = styled.p`
  && {
    color: ${previewTheme.body};
  }
  font-size: 7px;
  line-height: 1.5;
  text-align: justify;
  margin: 0;

  strong {
    color: ${pdfTheme.ink};
  }
`;

export const StyledDisclaimerTitle = styled.p`
  && {
    color: ${pdfTheme.ink};
  }
  font-size: 7.5px;
  font-weight: 700;
  text-align: center;
  text-decoration: underline;
  margin: 0 0 4px;
`;

export const StyledDisclaimerList = styled.ol`
  margin: 4px 0 0;
  padding-left: 12px;
  color: ${previewTheme.body};
  font-size: 7px;
  line-height: 1.5;

  strong {
    color: ${pdfTheme.ink};
  }
`;

/* margin-top: auto drops it to the foot of the page, as in the document. */
export const StyledSignLine = styled.div`
  width: 50%;
  margin: auto auto 0;
  border-top: 1px solid ${pdfTheme.ink};
  padding-top: 2px;
  text-align: center;
  font-size: 7px;
`;

export const StyledPageFooter = styled.div`
  border-top: 1px solid ${pdfTheme.lightGray};
  margin-top: 10px;
  padding-top: 4px;
  color: ${previewTheme.muted};
  font-size: 6.5px;
  text-align: center;
`;

/* Compact stand-in for the "Add Note for PDF" dialog. */
export const StyledDialogMock = styled.div`
  background-color: ${previewTheme.surface};
  border: 1px solid ${previewTheme.hairline};
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(23, 20, 63, 0.08);
  padding: 8px 10px;
  margin-bottom: 12px;
  font-size: 10px;
  color: ${previewTheme.body};

  strong {
    display: block;
    color: ${previewTheme.ink};
    font-size: 11px;
    margin-bottom: 6px;
  }
`;

export const StyledDialogField = styled.div<{ $short?: boolean }>`
  border: 1px solid ${previewTheme.hairline};
  border-radius: 4px;
  background-color: ${previewTheme.canvas};
  color: ${previewTheme.muted};
  padding: 4px 6px;
  margin-top: 4px;
  font-size: 9px;
  width: ${({ $short }) => ($short ? "64px" : "100%")};
`;

export const StyledDialogLabel = styled.span`
  display: block;
  color: ${previewTheme.muted};
  font-size: 9px;
  margin-top: 6px;
`;
